// src/components/templates/diary/DiaryContactsView.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { 
  Users, Mail, Phone, Plus, Search, Trash2, Edit2, User, MessageCircle,
  X, Save, Grid3X3, List, Star, StarOff, MapPin, Calendar, Building2,
  ExternalLink, Copy, MoreVertical, Filter, SortAsc, Tag, Briefcase,
  Heart, UserPlus, GraduationCap, Wrench, Target, Package, Globe,
  Video, Send, Linkedin, Twitter, Facebook, Instagram, ChevronDown,
  ChevronRight, FolderPlus, UsersRound, Archive, Share2, Eye, Lock,
  Check, Loader2, Download, Upload, Clock, PhoneCall, PhoneOutgoing,
  PhoneMissed, PhoneIncoming, MessageSquare, MailOpen, ArrowDownLeft, ArrowUpRight
} from 'lucide-react';
import { Button, Card, Badge, Input, Select, TextArea, Avatar } from './UIComponents';
import { 
  CONTACT_TYPES, PERSONAL_CATEGORIES, PROFESSIONAL_CATEGORIES, ALL_CATEGORIES,
  COMMUNICATION_CHANNELS, SOCIAL_PLATFORMS, CONTACT_VISIBILITY,
  DEFAULT_CONTACT, getCategoriesByType, getAvailableActions, formatPhoneForWhatsApp
} from '../../constants/contactTypes';

// --- LOG TYPES ---
const LOG_TYPES = {
  CALL: { id: 'call', label: 'Call', icon: PhoneCall, color: 'text-green-600', bg: 'bg-green-50' },
  MESSAGE: { id: 'message', label: 'Message', icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
  EMAIL: { id: 'email', label: 'Email', icon: MailOpen, color: 'text-purple-600', bg: 'bg-purple-50' }
};

const LOG_DIRECTIONS = {
  OUTGOING: { id: 'outgoing', label: 'Outgoing', icon: PhoneOutgoing },
  INCOMING: { id: 'incoming', label: 'Incoming', icon: PhoneCall }
};

const CALL_OUTCOMES = [
  { id: 'answered', label: 'Answered', icon: PhoneCall },
  { id: 'missed', label: 'Missed', icon: PhoneMissed },
  { id: 'voicemail', label: 'Voicemail', icon: PhoneOutgoing },
  { id: 'busy', label: 'Busy', icon: Phone }
];

// --- CATEGORY ICON MAPPING ---
const CATEGORY_ICONS = {
  family: Users, friend: Heart, acquaintance: UserPlus, mentor: GraduationCap,
  personal_service: Wrench, team: Users, client: Briefcase, lead: Target,
  supplier: Package, partner: Users, organisation: Building2, other: User
};

// --- LOG EDIT MODAL ---
const LogEditModal = ({ log, logType, onSave, onClose, onDelete, contactName }) => {
  const [notes, setNotes] = useState(log?.notes || '');
  const [outcome, setOutcome] = useState(log?.outcome || 'answered');
  const [duration, setDuration] = useState(log?.duration || '');
  const [subject, setSubject] = useState(log?.subject || '');
  const [followUpDate, setFollowUpDate] = useState(log?.followUpDate || '');
  const [direction, setDirection] = useState(log?.direction || 'outgoing');

  const typeInfo = LOG_TYPES[logType?.toUpperCase()] || LOG_TYPES.CALL;
  const TypeIcon = typeInfo.icon;

  const handleSave = () => {
    onSave({
      ...log,
      notes,
      outcome,
      duration,
      subject,
      followUpDate,
      direction,
      type: logType
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${typeInfo.bg}`}>
          <div className="flex items-center gap-2">
            <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
            <h3 className="font-semibold text-gray-900">
              {log?.id ? 'Edit' : 'Log'} {typeInfo.label}
              {contactName && <span className="font-normal text-gray-600"> - {contactName}</span>}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/50 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Timestamp */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            {log?.timestamp ? new Date(log.timestamp).toLocaleString() : new Date().toLocaleString()}
          </div>

          {/* Direction Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDirection('outgoing')}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border text-sm transition-all ${
                  direction === 'outgoing'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <ArrowUpRight className="w-4 h-4" />
                Outgoing
              </button>
              <button
                type="button"
                onClick={() => setDirection('incoming')}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border text-sm transition-all ${
                  direction === 'incoming'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <ArrowDownLeft className="w-4 h-4" />
                Incoming
              </button>
            </div>
          </div>

          {/* Call Outcome */}
          {logType === 'call' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Outcome</label>
              <div className="grid grid-cols-2 gap-2">
                {CALL_OUTCOMES.map(out => {
                  const OutcomeIcon = out.icon;
                  return (
                    <button
                      key={out.id}
                      type="button"
                      onClick={() => setOutcome(out.id)}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-all ${
                        outcome === out.id
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <OutcomeIcon className="w-4 h-4" />
                      {out.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Duration for calls */}
          {logType === 'call' && outcome === 'answered' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input
                type="text"
                value={duration}
                onChange={e => setDuration(e.target.value)}
                placeholder="e.g., 5 mins"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}

          {/* Subject for email/message */}
          {(logType === 'email' || logType === 'message') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Brief subject..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add details about this communication..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
          </div>

          {/* Follow-up */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
            <input
              type="date"
              value={followUpDate}
              onChange={e => setFollowUpDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t bg-gray-50 flex items-center justify-between rounded-b-xl">
          {log?.id ? (
            <button
              type="button"
              onClick={() => onDelete?.(log.id)}
              className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave}>
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- COMMUNICATION LOGS LIST ---
const CommunicationLogs = ({ logs = [], onEditLog, onDeleteLog }) => {
  const sortedLogs = [...logs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  if (sortedLogs.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-gray-400">
        No communication logs yet
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {sortedLogs.map(log => {
        const typeInfo = LOG_TYPES[log.type?.toUpperCase()] || LOG_TYPES.CALL;
        const TypeIcon = typeInfo.icon;
        const outcome = log.type === 'call' ? CALL_OUTCOMES.find(o => o.id === log.outcome) : null;
        const isIncoming = log.direction === 'incoming';

        return (
          <div
            key={log.id}
            onClick={() => onEditLog?.(log)}
            className="py-3 px-2 hover:bg-gray-50 cursor-pointer rounded-lg transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${typeInfo.bg} relative`}>
                <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                  isIncoming ? 'bg-blue-500' : 'bg-green-500'
                }`}>
                  {isIncoming 
                    ? <ArrowDownLeft className="w-2 h-2 text-white" />
                    : <ArrowUpRight className="w-2 h-2 text-white" />
                  }
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 text-sm">
                    {isIncoming ? 'Received' : 'Sent'} {typeInfo.label}
                    {outcome && <span className="text-gray-500 font-normal"> • {outcome.label}</span>}
                    {log.duration && <span className="text-gray-500 font-normal"> • {log.duration}</span>}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(log.timestamp).toLocaleDateString()}
                  </span>
                </div>
                {log.subject && (
                  <p className="text-sm text-gray-600 truncate">{log.subject}</p>
                )}
                {log.notes && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{log.notes}</p>
                )}
                {log.followUpDate && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                    <Calendar className="w-3 h-3" />
                    Follow-up: {new Date(log.followUpDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- ACTION BUTTON WITH BADGE ---
const ActionButton = ({ icon: Icon, count, onClick, color, title, href }) => {
  return (
    <a
      href={href}
      onClick={onClick}
      target={href?.startsWith('http') ? '_blank' : undefined}
      className={`relative p-2 ${color} rounded-lg transition-colors`}
      title={title}
    >
      <Icon className="w-4 h-4" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </a>
  );
};

// --- CONTACT CARD ---
const ContactCard = ({ 
  contact, 
  onEdit, 
  onDelete, 
  onFavorite, 
  onSelect, 
  isSelected, 
  viewMode, 
  showWorkspace,
  onLogCommunication,
  onUpdateContact 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [logType, setLogType] = useState(null);

  const categoryInfo = ALL_CATEGORIES[contact.category?.toUpperCase()] || ALL_CATEGORIES.OTHER;
  const CategoryIcon = CATEGORY_ICONS[contact.category] || User;
  const isProfessional = contact.contactType === CONTACT_TYPES.PROFESSIONAL;

  const logs = contact.communicationLogs || [];
  const callCount = logs.filter(l => l.type === 'call').length;
  const messageCount = logs.filter(l => l.type === 'message').length;
  const emailCount = logs.filter(l => l.type === 'email').length;

  const getWhatsAppUrl = () => {
    const phone = formatPhoneForWhatsApp(contact.phone);
    return `https://wa.me/${phone}`;
  };

  // Auto-create log when action is triggered
  const handleCallClick = (e) => {
    e.stopPropagation();
    const newLog = {
      id: `log-${Date.now()}`,
      type: 'call',
      direction: 'outgoing',
      timestamp: new Date().toISOString(),
      outcome: 'answered',
      notes: `Called ${contact.name}`,
      duration: ''
    };
    const updatedLogs = [...logs, newLog];
    onUpdateContact?.({ ...contact, communicationLogs: updatedLogs });
  };

  const handleMessageClick = (e) => {
    e.stopPropagation();
    const newLog = {
      id: `log-${Date.now()}`,
      type: 'message',
      direction: 'outgoing',
      timestamp: new Date().toISOString(),
      subject: 'WhatsApp message',
      notes: `Sent WhatsApp message to ${contact.name}`
    };
    const updatedLogs = [...logs, newLog];
    onUpdateContact?.({ ...contact, communicationLogs: updatedLogs });
  };

  const handleEmailClick = (e) => {
    e.stopPropagation();
    const newLog = {
      id: `log-${Date.now()}`,
      type: 'email',
      direction: 'outgoing',
      timestamp: new Date().toISOString(),
      subject: 'Email sent',
      notes: `Sent email to ${contact.name} at ${contact.email}`
    };
    const updatedLogs = [...logs, newLog];
    onUpdateContact?.({ ...contact, communicationLogs: updatedLogs });
  };

  const handleSaveLog = (logData) => {
    const updatedLogs = editingLog?.id && logs.some(l => l.id === editingLog.id)
      ? logs.map(l => l.id === editingLog.id ? { ...logData, id: l.id } : l)
      : [...logs, { ...logData, id: editingLog?.id || `log-${Date.now()}`, timestamp: logData.timestamp || new Date().toISOString() }];
    
    onUpdateContact?.({ ...contact, communicationLogs: updatedLogs });
    setEditingLog(null);
    setLogType(null);
  };

  const handleDeleteLog = (logId) => {
    if (window.confirm('Delete this log?')) {
      const updatedLogs = logs.filter(l => l.id !== logId);
      onUpdateContact?.({ ...contact, communicationLogs: updatedLogs });
      setEditingLog(null);
      setLogType(null);
    }
  };

  const handleEditLog = (log) => {
    setLogType(log.type);
    setEditingLog(log);
  };

  const handleLogIncoming = (type) => {
    const newLog = {
      id: `log-${Date.now()}`,
      type,
      direction: 'incoming',
      timestamp: new Date().toISOString(),
      outcome: type === 'call' ? 'answered' : undefined,
      notes: '',
      subject: ''
    };
    setLogType(type);
    setEditingLog(newLog);
  };

  if (viewMode === 'list') {
    return (
      <>
        <div className={`bg-white rounded-lg border overflow-hidden transition-all ${
          isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
        }`}>
          {/* Main Row */}
          <div 
            className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
            onClick={() => setExpanded(!expanded)}
          >
            {onSelect && (
              <button
                onClick={(e) => { e.stopPropagation(); onSelect(contact); }}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                  isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                }`}
              >
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </button>
            )}
            
            <Avatar name={contact.name} size="lg" color={contact.avatarColor || 'bg-indigo-500'} />
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 truncate">{contact.name}</h3>
                {contact.isFavorite && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                <Badge color={categoryInfo.color}>{categoryInfo.label}</Badge>
                {isProfessional && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">PRO</span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                {contact.professional?.position && (
                  <span className="flex items-center truncate">
                    <Briefcase className="w-3 h-3 mr-1" />{contact.professional.position}
                  </span>
                )}
                {contact.professional?.company && (
                  <span className="flex items-center truncate">
                    <Building2 className="w-3 h-3 mr-1" />{contact.professional.company}
                  </span>
                )}
                {contact.email && (
                  <span className="flex items-center truncate"><Mail className="w-3 h-3 mr-1" />{contact.email}</span>
                )}
                {contact.phone && (
                  <span className="flex items-center"><Phone className="w-3 h-3 mr-1" />{contact.phone}</span>
                )}
              </div>
            </div>

            {/* Action Buttons with Badges */}
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              {contact.phone && (
                <ActionButton
                  icon={Phone}
                  count={callCount}
                  href={`tel:${contact.phone}`}
                  onClick={handleCallClick}
                  color="text-green-600 hover:bg-green-50"
                  title="Call"
                />
              )}
              {contact.phone && (
                <ActionButton
                  icon={MessageCircle}
                  count={messageCount}
                  href={getWhatsAppUrl()}
                  onClick={handleMessageClick}
                  color="text-green-600 hover:bg-green-50"
                  title="WhatsApp"
                />
              )}
              {contact.email && (
                <ActionButton
                  icon={Mail}
                  count={emailCount}
                  href={`mailto:${contact.email}`}
                  onClick={handleEmailClick}
                  color="text-purple-600 hover:bg-purple-50"
                  title="Email"
                />
              )}
              <button onClick={() => onFavorite?.(contact)} className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg">
                {contact.isFavorite ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
              </button>
              <button onClick={() => onEdit?.(contact)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => onDelete?.(contact.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {/* Expanded Logs Section */}
          {expanded && (
            <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">Communication History</h4>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-400 mr-2">Log incoming:</span>
                  {contact.phone && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleLogIncoming('call'); }}
                      className="p-1.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                      title="Log incoming call"
                    >
                      <Phone className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {contact.phone && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleLogIncoming('message'); }}
                      className="p-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                      title="Log incoming message"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {contact.email && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleLogIncoming('email'); }}
                      className="p-1.5 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
                      title="Log incoming email"
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <CommunicationLogs 
                logs={logs} 
                onEditLog={handleEditLog}
                onDeleteLog={handleDeleteLog}
              />
            </div>
          )}
        </div>

        {/* Log Edit Modal */}
        {editingLog && (
          <LogEditModal
            log={editingLog}
            logType={logType}
            contactName={contact.name}
            onSave={handleSaveLog}
            onClose={() => { setEditingLog(null); setLogType(null); }}
            onDelete={handleDeleteLog}
          />
        )}
      </>
    );
  }

  // Grid/Card view
  return (
    <>
      <div className={`bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-lg transition-all group relative flex flex-col ${
        isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200'
      }`}>
        {/* Header Banner */}
        <div 
          className={`h-16 cursor-pointer ${contact.avatarColor || 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="absolute top-2 left-2 flex items-center gap-1">
            {contact.isFavorite && <Star className="w-4 h-4 text-white fill-white drop-shadow" />}
            {contact.visibility === 'workspace' && <UsersRound className="w-4 h-4 text-white drop-shadow" />}
          </div>
          {onSelect && (
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(contact); }}
              className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                isSelected ? 'border-white bg-white' : 'border-white/50 bg-white/20'
              }`}
            >
              {isSelected && <Check className="w-3 h-3 text-indigo-600" />}
            </button>
          )}
        </div>
        
        <div className="px-4 flex-1 flex flex-col relative">
          {/* Avatar */}
          <div className="absolute -top-6 left-4">
            <div className="w-12 h-12 rounded-full border-3 border-white shadow-md overflow-hidden">
              <Avatar name={contact.name} size="xl" color={contact.avatarColor || 'bg-indigo-500'} className="w-full h-full" />
            </div>
          </div>
          
          {/* Category & Type */}
          <div className="flex justify-end pt-2 gap-1">
            {isProfessional && (
              <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">PRO</span>
            )}
            <Badge color={categoryInfo.color}>{categoryInfo.label}</Badge>
          </div>

          {/* Name & Info */}
          <div className="mt-1 flex-1">
            <h3 className="text-base font-bold text-gray-900 truncate">{contact.name}</h3>
            
            {(contact.professional?.position || contact.professional?.company) && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {contact.professional.position}{contact.professional.position && contact.professional.company && ' • '}
                {contact.professional.company}
              </p>
            )}
            
            <div className="space-y-1 mt-3">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center text-xs text-gray-600 hover:text-indigo-600">
                  <Mail className="w-3 h-3 mr-1.5 text-gray-400" />
                  <span className="truncate">{contact.email}</span>
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center text-xs text-gray-600 hover:text-indigo-600">
                  <Phone className="w-3 h-3 mr-1.5 text-gray-400" />{contact.phone}
                </a>
              )}
            </div>

            {/* Channel Icons */}
            <div className="flex items-center gap-1 mt-2">
              {contact.channels?.whatsapp && (
                <a href={getWhatsAppUrl()} target="_blank" className="p-1 text-green-600 hover:bg-green-50 rounded">
                  <MessageCircle className="w-3.5 h-3.5" />
                </a>
              )}
              {contact.socials?.linkedin && (
                <a href={contact.socials.linkedin} target="_blank" className="p-1 text-blue-700 hover:bg-blue-50 rounded">
                  <Linkedin className="w-3.5 h-3.5" />
                </a>
              )}
              {contact.socials?.twitter && (
                <a href={contact.socials.twitter} target="_blank" className="p-1 text-sky-500 hover:bg-sky-50 rounded">
                  <Twitter className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            {/* Tags */}
            {contact.tags?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {contact.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full">{tag}</span>
                ))}
                {contact.tags.length > 3 && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full">+{contact.tags.length - 3}</span>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions with Badges */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100 mt-3">
            <div className="flex gap-1">
              {contact.phone && (
                <ActionButton
                  icon={Phone}
                  count={callCount}
                  href={`tel:${contact.phone}`}
                  onClick={handleCallClick}
                  color="text-green-600 hover:bg-green-50"
                  title="Call"
                />
              )}
              {contact.email && (
                <ActionButton
                  icon={Mail}
                  count={emailCount}
                  href={`mailto:${contact.email}`}
                  onClick={handleEmailClick}
                  color="text-purple-600 hover:bg-purple-50"
                  title="Email"
                />
              )}
              {contact.phone && (
                <ActionButton
                  icon={MessageCircle}
                  count={messageCount}
                  href={getWhatsAppUrl()}
                  onClick={handleMessageClick}
                  color="text-green-600 hover:bg-green-50"
                  title="WhatsApp"
                />
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={() => onFavorite?.(contact)} className={`p-1.5 rounded-lg ${contact.isFavorite ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500'}`}>
                <Star className={`w-4 h-4 ${contact.isFavorite ? 'fill-amber-500' : ''}`} />
              </button>
              <button onClick={() => onEdit?.(contact)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg">
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Logs */}
        {expanded && (
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-600">History</h4>
              <div className="flex items-center gap-1">
                {contact.phone && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLogIncoming('call'); }}
                    className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                    title="Log incoming call"
                  >
                    <Phone className="w-3 h-3" />
                  </button>
                )}
                {contact.phone && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLogIncoming('message'); }}
                    className="p-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    title="Log incoming message"
                  >
                    <MessageCircle className="w-3 h-3" />
                  </button>
                )}
                {contact.email && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLogIncoming('email'); }}
                    className="p-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                    title="Log incoming email"
                  >
                    <Mail className="w-3 h-3" />
                  </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); setExpanded(false); }} className="p-1 hover:bg-gray-200 rounded ml-1">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              </div>
            </div>
            <CommunicationLogs 
              logs={logs} 
              onEditLog={handleEditLog}
              onDeleteLog={handleDeleteLog}
            />
          </div>
        )}
      </div>

      {/* Log Edit Modal */}
      {editingLog && (
        <LogEditModal
          log={editingLog}
          logType={logType}
          contactName={contact.name}
          onSave={handleSaveLog}
          onClose={() => { setEditingLog(null); setLogType(null); }}
          onDelete={handleDeleteLog}
        />
      )}
    </>
  );
};

// --- COLLAPSIBLE SECTION ---
const Section = ({ id, title, icon: Icon, children, activeSection, setActiveSection }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={() => setActiveSection(activeSection === id ? null : id)}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-600" />
        <span className="font-medium text-gray-800">{title}</span>
      </div>
      {activeSection === id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
    </button>
    {activeSection === id && <div className="p-4 space-y-4">{children}</div>}
  </div>
);

// --- ENHANCED CONTACT FORM ---
const ContactForm = ({ contact, onSave, onCancel, workspaceContext, groups = [] }) => {
  const [formData, setFormData] = useState({
    ...DEFAULT_CONTACT,
    ...contact,
    workspaceId: contact?.workspaceId || workspaceContext?.workspaceId || null
  });
  const [activeSection, setActiveSection] = useState('basic');
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const colors = [
    'bg-indigo-500', 'bg-pink-500', 'bg-green-500', 'bg-blue-500', 
    'bg-amber-500', 'bg-purple-500', 'bg-red-500', 'bg-teal-500'
  ];

  const categories = getCategoriesByType(formData.contactType);

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      await onSave?.({
        id: contact?.id,
        ...formData,
        name: formData.name.trim()
      });
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (!tagInput.trim() || formData.tags.includes(tagInput.trim())) return;
    setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const updateNested = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">{contact ? 'Edit Contact' : 'New Contact'}</h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}><X className="w-4 h-4" /></Button>
          <Button onClick={handleSubmit} disabled={!formData.name.trim() || saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-2">{contact ? 'Update' : 'Create'}</span>
          </Button>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Contact Type Toggle */}
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, contactType: CONTACT_TYPES.PERSONAL, category: 'friend' }))}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
              formData.contactType === CONTACT_TYPES.PERSONAL ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            <User className="w-4 h-4" /> Personal
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, contactType: CONTACT_TYPES.PROFESSIONAL, category: 'team' }))}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
              formData.contactType === CONTACT_TYPES.PROFESSIONAL ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            <Briefcase className="w-4 h-4" /> Professional
          </button>
        </div>

        {/* Avatar & Basic Info - Always Visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Avatar Color</label>
            <div className="flex gap-2 flex-wrap">
              {colors.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, avatarColor: color }))}
                  className={`w-8 h-8 rounded-full ${color} transition-all ${
                    formData.avatarColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                  }`}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={formData.category}
              onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input 
            label="Full Name *" 
            value={formData.name} 
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Jane Doe"
          />
          <Input 
            label="Email" 
            type="email"
            value={formData.email} 
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="jane@example.com"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input 
            label="Phone" 
            value={formData.phone} 
            onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="+1 555 123 4567"
          />
          <Input 
            label="Birthday" 
            type="date"
            value={formData.birthday} 
            onChange={e => setFormData(prev => ({ ...prev, birthday: e.target.value }))}
          />
        </div>

        {/* Professional Section */}
        {formData.contactType === CONTACT_TYPES.PROFESSIONAL && (
          <Section id="professional" title="Professional Details" icon={Briefcase} activeSection={activeSection} setActiveSection={setActiveSection}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input 
                label="Company" 
                value={formData.professional?.company || ''} 
                onChange={e => updateNested('professional.company', e.target.value)}
                placeholder="Acme Inc."
              />
              <Input 
                label="Position" 
                value={formData.professional?.position || ''} 
                onChange={e => updateNested('professional.position', e.target.value)}
                placeholder="Marketing Manager"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input 
                label="Department" 
                value={formData.professional?.department || ''} 
                onChange={e => updateNested('professional.department', e.target.value)}
                placeholder="Marketing"
              />
              <Input 
                label="Work Phone" 
                value={formData.professional?.workPhone || ''} 
                onChange={e => updateNested('professional.workPhone', e.target.value)}
                placeholder="+1 555 987 6543"
              />
            </div>
            <Input 
              label="Work Email" 
              type="email"
              value={formData.professional?.workEmail || ''} 
              onChange={e => updateNested('professional.workEmail', e.target.value)}
              placeholder="jane@acme.com"
            />
          </Section>
        )}

        {/* Address Section */}
        <Section id="address" title="Address" icon={MapPin} activeSection={activeSection} setActiveSection={setActiveSection}>
          <Input 
            label="Street Address" 
            value={formData.address?.street || ''} 
            onChange={e => updateNested('address.street', e.target.value)}
            placeholder="123 Main St"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Input 
              label="City" 
              value={formData.address?.city || ''} 
              onChange={e => updateNested('address.city', e.target.value)}
              placeholder="San Francisco"
            />
            <Input 
              label="State" 
              value={formData.address?.state || ''} 
              onChange={e => updateNested('address.state', e.target.value)}
              placeholder="CA"
            />
            <Input 
              label="Zip" 
              value={formData.address?.zip || ''} 
              onChange={e => updateNested('address.zip', e.target.value)}
              placeholder="94102"
            />
            <Input 
              label="Country" 
              value={formData.address?.country || ''} 
              onChange={e => updateNested('address.country', e.target.value)}
              placeholder="USA"
            />
          </div>
        </Section>

        {/* Social Media Section */}
        <Section id="social" title="Social Media" icon={Globe} activeSection={activeSection} setActiveSection={setActiveSection}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Linkedin className="w-5 h-5 text-blue-700" />
              <Input 
                value={formData.socials?.linkedin || ''} 
                onChange={e => updateNested('socials.linkedin', e.target.value)}
                placeholder="linkedin.com/in/username"
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Twitter className="w-5 h-5 text-sky-500" />
              <Input 
                value={formData.socials?.twitter || ''} 
                onChange={e => updateNested('socials.twitter', e.target.value)}
                placeholder="twitter.com/username"
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Facebook className="w-5 h-5 text-blue-600" />
              <Input 
                value={formData.socials?.facebook || ''} 
                onChange={e => updateNested('socials.facebook', e.target.value)}
                placeholder="facebook.com/username"
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Instagram className="w-5 h-5 text-pink-600" />
              <Input 
                value={formData.socials?.instagram || ''} 
                onChange={e => updateNested('socials.instagram', e.target.value)}
                placeholder="instagram.com/username"
                className="flex-1"
              />
            </div>
          </div>
        </Section>

        {/* Tags Section */}
        <Section id="tags" title="Tags" icon={Tag} activeSection={activeSection} setActiveSection={setActiveSection}>
          <div className="flex gap-2 flex-wrap mb-3">
            {formData.tags?.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                {tag}
                <button type="button" onClick={() => removeTag(tag)} className="hover:text-indigo-900"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input 
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add a tag..."
              className="flex-1"
            />
            <Button type="button" variant="secondary" onClick={addTag}><Plus className="w-4 h-4" /></Button>
          </div>
        </Section>

        {/* Notes Section */}
        <Section id="notes" title="Notes" icon={Edit2} activeSection={activeSection} setActiveSection={setActiveSection}>
          <TextArea 
            value={formData.notes} 
            onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add any notes about this contact..."
            rows={4}
          />
        </Section>

        {/* Visibility & Groups */}
        {workspaceContext && (
          <Section id="visibility" title="Visibility & Groups" icon={Eye} activeSection={activeSection} setActiveSection={setActiveSection}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, visibility: 'private' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border text-sm ${
                      formData.visibility === 'private' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200'
                    }`}
                  >
                    <Lock className="w-4 h-4" /> Private
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, visibility: 'workspace' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border text-sm ${
                      formData.visibility === 'workspace' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200'
                    }`}
                  >
                    <UsersRound className="w-4 h-4" /> Workspace
                  </button>
                </div>
              </div>

              {groups.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Groups</label>
                  <div className="flex flex-wrap gap-2">
                    {groups.map(group => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => {
                          const newGroups = formData.groups?.includes(group.id)
                            ? formData.groups.filter(g => g !== group.id)
                            : [...(formData.groups || []), group.id];
                          setFormData(prev => ({ ...prev, groups: newGroups }));
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
                          formData.groups?.includes(group.id)
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {group.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Favorite Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isFavorite}
            onChange={e => setFormData(prev => ({ ...prev, isFavorite: e.target.checked }))}
            className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
          />
          <Star className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-gray-700">Mark as Favorite</span>
        </label>
      </div>
    </Card>
  );
};

// --- MAIN COMPONENT ---
export const DiaryContactsView = ({ 
  contacts = [],
  contactGroups = [],
  onAddContact,
  onUpdateContact,
  onDeleteContact,
  diaryService,
  contactService,
  bookId,
  customStyling = {},
  workspaceContext = null,
  isOwner = false
}) => {
  const [viewMode, setViewMode] = useState('LIST');
  const [displayMode, setDisplayMode] = useState('list');
  const [editingContact, setEditingContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [sortOrder, setSortOrder] = useState('name-asc');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Filtered & sorted contacts
  const filteredContacts = useMemo(() => {
    return contacts
      .filter(c => {
        const matchesSearch = !searchQuery || 
          c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.phone?.includes(searchQuery) ||
          c.professional?.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.professional?.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCategory = filterCategory === 'ALL' || c.category === filterCategory;
        const matchesType = filterType === 'ALL' || c.contactType === filterType;
        const matchesFavorite = !showFavoritesOnly || c.isFavorite;
        return matchesSearch && matchesCategory && matchesType && matchesFavorite;
      })
      .sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        if (sortOrder === 'name-asc') return a.name.localeCompare(b.name);
        if (sortOrder === 'name-desc') return b.name.localeCompare(a.name);
        if (sortOrder === 'category') return (a.category || '').localeCompare(b.category || '');
        if (sortOrder === 'company') return (a.professional?.company || '').localeCompare(b.professional?.company || '');
        return 0;
      });
  }, [contacts, searchQuery, filterCategory, filterType, showFavoritesOnly, sortOrder]);

  // Stats
  const stats = useMemo(() => ({
    total: contacts.length,
    favorites: contacts.filter(c => c.isFavorite).length,
    personal: contacts.filter(c => c.contactType === CONTACT_TYPES.PERSONAL).length,
    professional: contacts.filter(c => c.contactType === CONTACT_TYPES.PROFESSIONAL).length,
    byCategory: contacts.reduce((acc, c) => {
      acc[c.category] = (acc[c.category] || 0) + 1;
      return acc;
    }, {})
  }), [contacts]);

  const handleSave = async (contactData) => {
    if (editingContact) {
      onUpdateContact?.(contactData);
    } else {
      onAddContact?.({ ...contactData, createdAt: new Date().toISOString() });
    }
    setEditingContact(null);
    setViewMode('LIST');
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Delete this contact?')) {
      onDeleteContact?.(contactId);
    }
  };

  const handleFavorite = async (contact) => {
    onUpdateContact?.({ ...contact, isFavorite: !contact.isFavorite });
  };

  const handleSelectContact = (contact) => {
    setSelectedContacts(prev => 
      prev.includes(contact.id) 
        ? prev.filter(id => id !== contact.id)
        : [...prev, contact.id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedContacts.length} contacts?`)) {
      selectedContacts.forEach(id => onDeleteContact?.(id));
      setSelectedContacts([]);
    }
  };

  const categoryFilters = [
    { key: 'ALL', label: 'All', count: contacts.length },
    ...Object.values(ALL_CATEGORIES).map(cat => ({
      key: cat.id,
      label: cat.label,
      count: stats.byCategory[cat.id] || 0
    }))
  ];

  return (
    <div className="flex-1 overflow-y-auto flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-6 h-6 mr-2 text-indigo-600" />
              Contacts
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {stats.total} contacts • {stats.personal} personal • {stats.professional} professional
              {stats.favorites > 0 && ` • ${stats.favorites} favorites`}
            </p>
          </div>
          
          {viewMode === 'LIST' ? (
            <div className="flex gap-2">
              {selectedContacts.length > 0 && (
                <Button variant="danger" onClick={handleBulkDelete}>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedContacts.length})
                </Button>
              )}
              <Button onClick={() => { setEditingContact(null); setViewMode('FORM'); }} style={{ backgroundColor: customStyling.primaryColor }}>
                <Plus className="w-4 h-4 mr-2" /> Add Contact
              </Button>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => { setEditingContact(null); setViewMode('LIST'); }}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'LIST' ? (
        <>
          {/* Filters & Search */}
          <div className="bg-white border-b border-gray-200 px-6 py-3">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search contacts..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Type Filter */}
                <select
                  value={filterType}
                  onChange={e => setFilterType(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="ALL">All Types</option>
                  <option value={CONTACT_TYPES.PERSONAL}>Personal</option>
                  <option value={CONTACT_TYPES.PROFESSIONAL}>Professional</option>
                </select>

                {/* Category Filter */}
                <select
                  value={filterCategory}
                  onChange={e => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  {categoryFilters.map(f => (
                    <option key={f.key} value={f.key}>{f.label} ({f.count})</option>
                  ))}
                </select>

                {/* Favorites Toggle */}
                <button
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                  className={`p-2 rounded-lg border transition-all ${
                    showFavoritesOnly ? 'bg-amber-50 border-amber-200 text-amber-600' : 'border-gray-200 text-gray-400'
                  }`}
                >
                  <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-amber-500' : ''}`} />
                </button>

                {/* View Toggle */}
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setDisplayMode('grid')}
                    className={`p-2 ${displayMode === 'grid' ? 'bg-gray-100' : ''}`}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDisplayMode('list')}
                    className={`p-2 ${displayMode === 'list' ? 'bg-gray-100' : ''}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact List */}
          <div className="p-6">
            {filteredContacts.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No contacts found</h3>
                <p className="text-gray-500 mt-1">
                  {searchQuery ? 'Try adjusting your search' : 'Add your first contact to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setViewMode('FORM')} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Add Contact
                  </Button>
                )}
              </div>
            ) : displayMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredContacts.map(contact => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    viewMode="grid"
                    onEdit={c => { setEditingContact(c); setViewMode('FORM'); }}
                    onDelete={handleDelete}
                    onFavorite={handleFavorite}
                    onSelect={selectedContacts.length > 0 ? handleSelectContact : null}
                    isSelected={selectedContacts.includes(contact.id)}
                    onUpdateContact={onUpdateContact}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredContacts.map(contact => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    viewMode="list"
                    onEdit={c => { setEditingContact(c); setViewMode('FORM'); }}
                    onDelete={handleDelete}
                    onFavorite={handleFavorite}
                    onSelect={handleSelectContact}
                    isSelected={selectedContacts.includes(contact.id)}
                    onUpdateContact={onUpdateContact}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <ContactForm 
              contact={editingContact}
              onSave={handleSave}
              onCancel={() => { setEditingContact(null); setViewMode('LIST'); }}
              workspaceContext={workspaceContext}
              groups={contactGroups}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DiaryContactsView;